import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { useAuth } from '../../lib/AuthContext';
import { useSession } from '../../lib/SessionContext';
import colors from '../../lib/colors';
import { msg } from '@lingui/core/macro';
import {
    type LocalizedMessage,
    useResolvedMessage,
} from '../../lib/i18n/messages';

export interface FullPageSignInGateProps {
    redirectTo: string;
    mode?: 'signin' | 'signup';
    title?: LocalizedMessage;
    message?: LocalizedMessage;
    primaryActionLabel?: LocalizedMessage;
    loginHint?: string;
}

export default function FullPageSignInGate({
    redirectTo,
    mode: preferredMode,
    title = msg({
        id: 'auth.signInGate.defaultTitle',
        message: 'Sign in to access this page',
        comment: 'Default title for a private page sign-in gate.',
    }),
    message = msg({
        id: 'auth.signInGate.defaultMessage',
        message: 'This area is part of your private Paperzilla workspace.',
        comment: 'Default explanation shown on a private page sign-in gate.',
    }),
    primaryActionLabel = msg({
        id: 'auth.signInGate.defaultAction',
        message: 'Sign in',
        comment: 'Default primary action on a private page sign-in gate.',
    }),
    loginHint,
}: FullPageSignInGateProps) {
    const { openAuth } = useAuth();
    const { session, isReturningUser } = useSession();
    const resolvedTitle = useResolvedMessage(title);
    const resolvedMessage = useResolvedMessage(message);
    const resolvedPrimaryActionLabel = useResolvedMessage(primaryActionLabel);

    const handleSignIn = () => {
        const mode = preferredMode ?? (session
            ? 'signup'
            : isReturningUser
                ? 'signin'
                : 'signup');

        openAuth({
            mode,
            redirectTo,
            loginHint,
        });
    };

    return (
        <View style={styles.container}>
            <View style={styles.card}>
                <Text style={styles.title}>{resolvedTitle}</Text>
                <Text style={styles.message}>{resolvedMessage}</Text>

                <TouchableOpacity
                    style={styles.button}
                    onPress={handleSignIn}
                    activeOpacity={0.8}
                    testID="app-route-sign-in-button"
                >
                    <Text style={styles.buttonText}>{resolvedPrimaryActionLabel}</Text>
                </TouchableOpacity>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.bgWeak,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 20,
    },
    card: {
        width: '100%',
        maxWidth: 520,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 14,
        paddingHorizontal: 24,
        paddingVertical: 24,
    },
    title: {
        fontSize: 24,
        lineHeight: 30,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        marginBottom: 12,
        textAlign: 'center',
    },
    message: {
        fontSize: 15,
        lineHeight: 22,
        color: colors.text700,
        textAlign: 'center',
        marginBottom: 20,
    },
    button: {
        alignSelf: 'center',
        backgroundColor: colors.primaryGreen,
        borderRadius: 10,
        paddingHorizontal: 18,
        paddingVertical: 10,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    buttonText: {
        color: '#fff',
        fontSize: 15,
        fontWeight: '600',
    },
});
